import React, { useState, useMemo } from 'react';
import { Pagination, PaginationItem, PaginationLink, Input, Modal, ModalHeader, ModalBody } from 'reactstrap';
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import '../ProfileQuestions/ProfileQuestions.css';

const formatDate = (isoString) => {
    if (!isoString) return '-';
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return '-';
    
    // Time format e.g. 1:30 PM
    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true });
    // Date format e.g. Aug 8 2026
    const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

    return (
        <div style={{ lineHeight: '1.2' }}>
            <span style={{ fontWeight: 600 }}>{time}</span>
            <br />
            <span style={{ fontSize: '12px', color: '#6c757d' }}>{date}</span>
        </div>
    );
};

const getStatusBadge = (status) => {
    switch(status) {
        case 'success': return <span className="status-badge status-2xx">Success</span>;
        case 'failed': return <span className="status-badge status-4xx">Failed</span>;
        case 'opened': return <span className="status-badge status-3xx">Opened</span>;
        case 'dismissed': return <span className="status-badge status-5xx">Dismissed</span>;
        default: return <span className="status-badge">{status}</span>;
    }
};

const getHistoryStatusStyle = (status) => {
    switch(status?.toLowerCase()) {
        case 'sent': 
        case 'success':
            return { backgroundColor: 'rgba(34, 197, 94, 0.15)', color: '#16a34a', border: '1px solid rgba(34, 197, 94, 0.3)' };
        case 'delivered':
            return { backgroundColor: 'rgba(168, 85, 247, 0.15)', color: '#9333ea', border: '1px solid rgba(168, 85, 247, 0.3)' }; // Purple
        case 'opened':
            return { backgroundColor: 'rgba(59, 130, 246, 0.15)', color: '#2563eb', border: '1px solid rgba(59, 130, 246, 0.3)' };
        case 'failed':
            return { backgroundColor: 'rgba(239, 68, 68, 0.15)', color: '#dc2626', border: '1px solid rgba(239, 68, 68, 0.3)' };
        case 'dismissed':
            return { backgroundColor: 'rgba(107, 114, 128, 0.15)', color: '#4b5563', border: '1px solid rgba(107, 114, 128, 0.3)' };
        default:
            return { backgroundColor: '#f9fafb', color: '#4b5563', border: '1px solid #e5e7eb' };
    }
};

    const FeatureList = ({ data, totalRecords, totalPages, currentPage, itemsPerPage, onPageChange, onItemsPerPageChange }) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });

    const toggleModal = () => setModalOpen(!modalOpen);

    const handleView = (item) => {
        setSelectedItem(item);
        toggleModal();
    };

    const handleSort = (field) => {
        let direction = 'asc';
        if (sortConfig.key === field && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key: field, direction });
    };

    const sortedData = useMemo(() => {
        if (!data) return [];
        let sortableItems = [...data];
        if (sortConfig.key) {
            sortableItems.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                if (sortConfig.key === 'modifiedDate') {
                    aValue = new Date(aValue).getTime() || 0;
                    bValue = new Date(bValue).getTime() || 0;
                } else {
                    aValue = aValue ? aValue.toString().toLowerCase() : '';
                    bValue = bValue ? bValue.toString().toLowerCase() : '';
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [data, sortConfig]);

    const startIndex = (currentPage - 1) * itemsPerPage;

    const renderSortHeader = (label, field) => (
        <th style={{ whiteSpace: 'nowrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>{label}</span>
                <span style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', cursor: 'pointer', gap: '4px' }}>
                    <ChevronUp size={16} strokeWidth={3} color={sortConfig.key === field && sortConfig.direction === 'asc' ? '#0c0c0cff' : '#757373ff'} onClick={() => handleSort(field)} />
                    <ChevronDown size={16} strokeWidth={3} color={sortConfig.key === field && sortConfig.direction === 'desc' ? '#0c0c0cff' : '#757373ff'} onClick={() => handleSort(field)} />
                </span>
            </div>
        </th>
    );

    return (
        <div className="pq-table-wrapper mt-4">
            <div style={{ maxHeight: '40vh', overflowY: 'auto', padding: '0 20px' }}>
                <table className="pq-table">
                    <thead>
                        <tr>
                            <th>NO</th>
                            <th>USER ID</th>
                            {renderSortHeader('TITLE', 'title')}
                            {renderSortHeader('BODY', 'body')}
                            <th>FEATURE</th>
                            <th>STATUS</th>
                            {renderSortHeader('MODIFIED DATE', 'modifiedDate')}
                            <th style={{ textAlign: 'center' }}>ACTIONS</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedData && sortedData.length > 0 ? sortedData.map((item, index) => (
                            <tr key={index}>
                                <td>{startIndex + index + 1}</td>
                                <td>{item.userId}</td>
                                <td>{item.title}</td>
                                <td style={{ maxWidth: '250px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={item.body}>
                                    {item.body}
                                </td>
                                <td><span style={{ textTransform: 'capitalize', fontWeight: 600 }}>{item.feature}</span></td>
                                <td>{getStatusBadge(item.status)}</td>
                                <td>{formatDate(item.modifiedDate)}</td>
                                <td>
                                    <div className="d-flex justify-content-center">
                                        <button 
                                            className="pq-action-btn" 
                                            title="View Audit Log"
                                            onClick={() => handleView(item)}
                                        >
                                            <i className="fa fa-eye" style={{ color: '#8b5cf6' }}></i>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan="8" style={{ textAlign: 'center', padding: '40px' }}>
                                    No records found matching your filters.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination Footer */}
            {totalRecords > 0 && (
                <div className="pq-pagination-container d-flex justify-content-between align-items-center p-3 border-top">
                    <div className="d-flex align-items-center">
                        <span className="pq-pagination-text me-2">
                            Showing {startIndex + 1} – {Math.min(startIndex + itemsPerPage, totalRecords)} of {totalRecords} entries. Per page: 
                        </span>
                        <Input 
                            type="select" 
                            value={itemsPerPage} 
                            onChange={(e) => onItemsPerPageChange(Number(e.target.value))} 
                            className="pq-filter-input" 
                            style={{ width: '70px', display: 'inline-block', height: '32px !important', padding: '0 10px !important', margin: 0 }}
                        >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                        </Input>
                    </div>

                    <Pagination size="sm" className="mb-0">
                        <PaginationItem className="pq-page-item" disabled={currentPage === 1}>
                            <PaginationLink className="pq-page-link" onClick={() => onPageChange(currentPage - 1)}>
                                <ChevronLeft size={18} color={currentPage === 1 ? '#ccc' : '#000'} style={{ strokeWidth: 2.5 }} />
                            </PaginationLink>
                        </PaginationItem>

                        {[...Array(totalPages)].map((_, i) => {
                            if (i === 0 || i === totalPages - 1 || (i >= currentPage - 2 && i <= currentPage)) {
                                return (
                                    <PaginationItem className="pq-page-item" active={i + 1 === currentPage} key={i}>
                                        <PaginationLink className="pq-page-link" onClick={() => onPageChange(i + 1)}>
                                            {i + 1}
                                        </PaginationLink>
                                    </PaginationItem>
                                );
                            }
                            if (i === 1 && currentPage > 3) return <PaginationItem className="pq-page-item" disabled key="start-ellipsis"><PaginationLink className="pq-page-link">...</PaginationLink></PaginationItem>;
                            if (i === totalPages - 2 && currentPage < totalPages - 2) return <PaginationItem className="pq-page-item" disabled key="end-ellipsis"><PaginationLink className="pq-page-link">...</PaginationLink></PaginationItem>;
                            return null;
                        })}

                        <PaginationItem className="pq-page-item" disabled={currentPage === totalPages || totalPages === 0}>
                            <PaginationLink className="pq-page-link" onClick={() => onPageChange(currentPage + 1)}>
                                <ChevronRight size={18} color={(currentPage === totalPages || totalPages === 0) ? '#ccc' : '#000'} style={{ strokeWidth: 2.5 }} />
                            </PaginationLink>
                        </PaginationItem>
                    </Pagination>
                </div>
            )}

            {/* View Modal */}
            <Modal isOpen={modalOpen} toggle={toggleModal} size="lg" centered>
                <ModalHeader toggle={toggleModal}>Feature Log Details</ModalHeader>
                <ModalBody>
                    {selectedItem && (
                        <div>
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <p className="mb-1" style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.5px', color: '#000' }}>USER ID</p>
                                    <p style={{ fontWeight: 500 }}>{selectedItem.userId}</p>
                                </div>
                                <div className="col-md-6">
                                    <p className="mb-1" style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.5px', color: '#000' }}>FEATURE</p>
                                    <p style={{ fontWeight: 500, textTransform: 'capitalize' }}>{selectedItem.feature}</p>
                                </div>
                            </div>
                            
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <p className="mb-1" style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.5px', color: '#000' }}>STATUS</p>
                                    <div>{getStatusBadge(selectedItem.status)}</div>
                                </div>
                                <div className="col-md-6">
                                    <p className="mb-1" style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.5px', color: '#000' }}>SCREEN DESTINATION</p>
                                    <p style={{ fontWeight: 500 }}>{selectedItem.screenDestination || 'N/A'}</p>
                                </div>
                            </div>

                            <div className="row mb-3">
                                <div className="col-md-12">
                                    <p className="mb-1" style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.5px', color: '#000' }}>TITLE</p>
                                    <p style={{ fontWeight: 500 }}>{selectedItem.title}</p>
                                </div>
                            </div>

                            <div className="row mb-3">
                                <div className="col-md-12">
                                    <p className="mb-1" style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.5px', color: '#000' }}>BODY</p>
                                    <div className="p-3 bg-light rounded" style={{ fontWeight: 500, border: '1px solid #e5e7eb' }}>
                                        {selectedItem.body}
                                    </div>
                                </div>
                            </div>

                            <div className="row mb-3">
                                <div className="col-md-12">
                                    <p className="mb-2" style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.5px', color: '#000' }}>STATUS HISTORY</p>
                                    <div className="d-flex align-items-center flex-wrap" style={{ gap: '10px' }}>
                                        {selectedItem.statusHistory && selectedItem.statusHistory.length > 0 ? (
                                            selectedItem.statusHistory.map((historyItem, idx) => {
                                                const styleObj = getHistoryStatusStyle(historyItem.status);
                                                return (
                                                    <React.Fragment key={idx}>
                                                        <div className="p-2 rounded" style={{ backgroundColor: styleObj.backgroundColor, border: styleObj.border, minWidth: '120px', textAlign: 'center' }}>
                                                            <div style={{ textTransform: 'capitalize', fontWeight: 700, color: styleObj.color, marginBottom: '4px' }}>
                                                                {historyItem.status}
                                                            </div>
                                                            <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: 500 }}>
                                                                {formatDate(historyItem.timestamp)}
                                                            </div>
                                                        </div>
                                                        {idx < selectedItem.statusHistory.length - 1 && (
                                                            <i className="fa fa-arrow-right text-muted" style={{ fontSize: '14px' }}></i>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })
                                        ) : (
                                            <p className="text-muted mb-0" style={{ fontSize: '14px' }}>No history available</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-12 text-end mt-2 pt-3 border-top">
                                    <p className="mb-0" style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.5px', color: '#000' }}>LAST MODIFIED</p>
                                    <div className="d-flex justify-content-end align-items-center mt-1">
                                        <i className="fa fa-clock me-2 text-muted"></i>
                                        <div style={{ textAlign: 'right' }}>{formatDate(selectedItem.modifiedDate)}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </ModalBody>
            </Modal>
        </div>
    );
};

export default FeatureList;
